export const LISA_THEME_KEYS = [
  "primary-color",
  "secondary-color",
  "success-color",
  "warning-color",
  "danger-color",
  "info-color",
  "lisa-valid",
  "lisa-neutral",
  "lisa-invalid",
  "border-radius",
] as const;

export type LisaThemeKey = (typeof LISA_THEME_KEYS)[number];
export type LisaThemeType = Record<LisaThemeKey, string>;

export type LISAColorPaletteEntry = {
  name: string;
  hex: string;
};

export type LISAColorPalette = {
  designColors: Record<string, LISAColorPaletteEntry>;
  standardColors: Record<string, LISAColorPaletteEntry>;
  standardColorsFull: Record<string, LISAColorPaletteEntry>;
};

export const DEFAULT_LISA_THEME: Readonly<LisaThemeType> = Object.freeze({
  "primary-color": "#1e7faf",
  "secondary-color": "#a7b9c9",
  "success-color": "#3cb878",
  "warning-color": "#ffb347",
  "danger-color": "#e84c3d",
  "info-color": "#36a9e1",
  "border-radius": "2px",
  "lisa-valid": "#198754",
  "lisa-neutral": "#FFC107",
  "lisa-invalid": "#dc3545",
});

type LisaThemeExport = LisaThemeType & {
  ripple: boolean;
  colorPalette: LISAColorPalette;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function normalizeLISATheme(
  value: unknown,
  fallback: LisaThemeType = { ...DEFAULT_LISA_THEME },
): LisaThemeType {
  const source = isRecord(value) ? value : {};

  return Object.fromEntries(
    LISA_THEME_KEYS.map((key) => [
      key,
      typeof source[key] === "string" && source[key].length > 0 ? source[key] : fallback[key],
    ]),
  ) as LisaThemeType;
}

function normalizeLISAColorPaletteEntries(
  value: unknown,
  fallback: Record<string, LISAColorPaletteEntry>,
): Record<string, LISAColorPaletteEntry> {
  const normalized = Object.fromEntries(
    Object.entries(fallback).map(([key, entry]) => [key, { ...entry }]),
  );

  if (!isRecord(value)) {
    return normalized;
  }

  Object.entries(value).forEach(([key, entry]) => {
    if (!isRecord(entry) || typeof entry.name !== "string" || typeof entry.hex !== "string") {
      return;
    }

    normalized[key] = { name: entry.name, hex: entry.hex };
  });

  return normalized;
}

export function normalizeLISAColorPalette(
  value: unknown,
  fallback: LISAColorPalette = {
    designColors: {},
    standardColors: {},
    standardColorsFull: {},
  },
): LISAColorPalette {
  const source = isRecord(value) ? value : {};

  return {
    designColors: normalizeLISAColorPaletteEntries(source.designColors, fallback.designColors),
    standardColors: normalizeLISAColorPaletteEntries(
      source.standardColors,
      fallback.standardColors,
    ),
    standardColorsFull: normalizeLISAColorPaletteEntries(
      source.standardColorsFull,
      fallback.standardColorsFull,
    ),
  };
}

export function createLISAThemeExport(
  theme: unknown,
  ripple: boolean,
  colorPalette: unknown,
): LisaThemeExport {
  return {
    ...normalizeLISATheme(theme),
    ripple,
    colorPalette: normalizeLISAColorPalette(colorPalette),
  };
}

export function parseLISAThemeExport(
  value: unknown,
  fallback: LisaThemeType,
  fallbackRipple: boolean,
  fallbackColorPalette: LISAColorPalette,
): LisaThemeExport {
  const source = isRecord(value) ? value : {};

  return {
    ...normalizeLISATheme(source, fallback),
    ripple: typeof source.ripple === "boolean" ? source.ripple : fallbackRipple,
    colorPalette: normalizeLISAColorPalette(source.colorPalette, fallbackColorPalette),
  };
}
